import styled from 'styled-components'

export const Page = styled.div`
  min-height: calc(100vh - 180px);
  padding: 32px 16px 56px;

  @media (max-width: 767px) {
    padding: 0 0 32px;
  }
`

export const Shell = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
`

export const Hero = styled.div`
  background:
    linear-gradient(180deg, ${props => props.theme.palette.colors.surfaceElevatedStrong} 0%, ${props => props.theme.palette.primary.main} 100%);
  border-radius: 18px 18px 0 0;
  padding: 28px 32px;
  border: 1px solid ${props => props.theme.palette.colors.borderSoft};
  border-bottom: 0;
  box-shadow: 0 20px 40px ${props => props.theme.palette.colors.shadowStrong};

  @media (max-width: 767px) {
    padding: 24px 20px;
  }

  img,
  svg {
    max-width: 100%;
    height: auto;
  }
`

export const Panel = styled.div`
  background: ${props => props.theme.palette.colors.surfaceSoft};
  border: 1px solid ${props => props.theme.palette.colors.borderSoft};
  border-top: 0;
  border-radius: 0 0 18px 18px;
  padding: 24px;
  box-shadow: 0 24px 48px ${props => props.theme.palette.colors.shadowStrong};

  @media (max-width: 767px) {
    padding: 12px;
  }
`

export const PanelInner = styled.div`
  background: ${props => props.theme.palette.colors.surfaceElevated};
  border: 1px solid ${props => props.theme.palette.colors.borderSoft};
  border-radius: 16px;
  padding: 28px;

  @media (max-width: 767px) {
    padding: 14px;
  }
`

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`

export const HeaderTitle = styled.div`
  font-family: Poppins, sans-serif;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 700;
  color: ${props => props.theme.palette.colors.text};

  @media (max-width: 767px) {
    font-size: 24px;
  }
`

export const HeaderSubtitle = styled.div`
  font-size: 16px;
  color: ${props => props.theme.palette.colors.muted};
  margin-top: 6px;
`

export const HeaderMeta = styled.div`
  font-size: 13px;
  color: ${props => props.theme.palette.colors.muted};
  white-space: nowrap;

  strong {
    color: ${props => props.theme.palette.colors.text};
  }

  @media (max-width: 767px) {
    white-space: normal;
  }
`

export const TableCard = styled.div`
  border: 1px solid ${props => props.theme.palette.colors.borderSoft};
  border-radius: 12px;
  overflow: hidden;
  background: ${props => props.theme.palette.colors.surfaceElevatedStrong};

  @media (max-width: 767px) {
    border: 0;
    overflow: visible;
    background: transparent;
  }
`

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.palette.colors.subdued} ${props => props.theme.palette.colors.surfaceElevatedStrong};

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.palette.colors.surfaceElevatedStrong};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.palette.colors.subdued};
    border-radius: 8px;
  }

  @media (max-width: 767px) {
    overflow: visible;
  }
`

export const Table = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  font-family: Arial, Helvetica, sans-serif;

  thead tr {
    background: ${props => props.theme.palette.colors.panelBackdrop};
  }

  th {
    padding: 12px;
    text-align: left;
    font-size: 12px;
    color: ${props => props.theme.palette.colors.muted};
    border-bottom: 1px solid ${props => props.theme.palette.colors.borderSoft};
    white-space: nowrap;
    font-weight: 700;
    letter-spacing: 0;
  }

  th:last-child {
    text-align: center;
  }

  td {
    padding: 12px;
    font-size: 13px;
    color: ${props => props.theme.palette.colors.text};
    border-bottom: 1px solid ${props => props.theme.palette.colors.borderSoft};
    vertical-align: middle;
    font-weight: 500;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  tbody tr {
    background: ${props => props.theme.palette.colors.surfaceElevatedStrong};
    transition: background-color 0.16s ease;
  }

  tbody tr:hover {
    background: ${props => props.theme.palette.colors.badgeMuted};
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  @media (max-width: 767px) {
    display: block;
    min-width: 0;
    background: transparent;

    thead {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    tbody {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    tbody tr {
      display: block;
      border: 1px solid ${props => props.theme.palette.colors.borderSoft};
      border-radius: 14px;
      overflow: hidden;
      background: ${props => props.theme.palette.colors.surfaceElevatedStrong};
      box-shadow: 0 14px 30px ${props => props.theme.palette.colors.shadowStrong};
    }

    tbody tr:hover {
      background: ${props => props.theme.palette.colors.surfaceElevatedStrong};
    }

    td {
      display: grid;
      grid-template-columns: minmax(82px, 34%) minmax(0, 1fr);
      gap: 12px;
      align-items: start;
      padding: 12px 14px;
      font-size: 14px;
      line-height: 1.45;
      white-space: normal;
      border-bottom: 1px solid ${props => props.theme.palette.colors.borderSoft};
    }

    td::before {
      content: attr(data-label);
      color: ${props => props.theme.palette.colors.subdued};
      font-family: Poppins, sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0;
      text-transform: uppercase;
    }

    td[data-label="Actions"] {
      display: block;
      padding-top: 14px;
      background: ${props => props.theme.palette.colors.panelBackdrop};
    }

    td[data-label="Actions"]::before {
      display: none;
    }

    tbody tr:last-child td {
      border-bottom: 1px solid ${props => props.theme.palette.colors.borderSoft};
    }

    tbody tr td:last-child {
      border-bottom: 0;
    }
  }
`

export const EmailLink = styled.a`
  color: ${props => props.theme.palette.colors.text};
  text-decoration: underline;
  text-decoration-color: ${props => props.theme.palette.blue.main};
  text-underline-offset: 2px;
  word-break: break-word;
  overflow-wrap: anywhere;

  &:hover {
    color: ${props => props.theme.palette.blue.main};
  }
`

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  min-width: 196px;
  overflow-wrap: normal;
  word-break: normal;
  white-space: nowrap;

  .MuiButton-root {
    white-space: nowrap;
    overflow-wrap: normal;
    word-break: normal;
  }

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;

    .MuiButton-root {
      width: 100% !important;
    }
  }
`

export const DecisionState = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 196px;
  min-height: 32px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${props => props.approved ? props.theme.palette.success.main : props.theme.palette.error.main};
  background: ${props => props.theme.palette.colors.badgeMuted};
  color: ${props => props.approved ? props.theme.palette.success.main : props.theme.palette.error.main};
  font-size: 13px;
  font-weight: 700;

  @media (max-width: 767px) {
    width: 100%;
    min-width: 0;
  }
`

export const StateCard = styled.div`
  border: 1px solid ${props => props.theme.palette.colors.borderSoft};
  background: ${props => props.compact ? props.theme.palette.colors.surfaceElevatedStrong : props.theme.palette.colors.surfaceElevated};
  border-radius: 14px;
  padding: 28px;
  text-align: center;
`

export const StateTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.palette.colors.text};
  margin-bottom: 10px;
`

export const StateText = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: ${props => props.theme.palette.colors.muted};
`

export const FootNote = styled.p`
  margin: 16px 0 0 0;
  font-size: 12px;
  line-height: 18px;
  color: ${props => props.theme.palette.colors.subdued};
`
